// RELIABILITY-1 — the silent load failures, made visible and made recoverable.
//
// THE PROBLEM THIS SOLVES. Ten pages render LoadErrorCard from a manual
// useEffect + try/catch. They do not use react-query, so queryClient's
// `retry: 1` and `refetchOnWindowFocus` never applied to them: a single failed
// fetch stuck until a human pressed a button. And every one of those catches
// was bare — `} catch {` — so the failure was invisible in Sentry, in logs,
// and to us. We had a bug we could not see and could not recover from.
//
// WHAT LIVES HERE vs WHAT LIVES IN LoadErrorCard, because the split matters:
//
//   HERE          the things that need the ERROR OBJECT — classifying it,
//                 reporting it, and the one recovery that must happen BEFORE
//                 the user ever sees an error screen: refreshing an expired
//                 session and retrying once.
//
//   LoadErrorCard the things that only need "it failed" — bounded auto-retry
//                 with backoff, and retry on window focus. Those live in the
//                 card because it already receives onRetry, so all ten pages
//                 got them with no call-site change and an eleventh page gets
//                 them for free.
//
// THERE IS DELIBERATELY NO RETRY LOOP IN THIS FILE. The card retries. If both
// retried they would compound into a burst against a struggling backend, which
// is the opposite of the point.

import { captureException } from './report'
import { supabase } from './supabase'

declare const __BUILD_ID__: string

/** The build this tab is running. A cluster of failures sharing one build id
 *  in the minutes after a deploy is the stale-chunk signature. */
export const BUILD_ID: string = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'unknown'

/**
 * WHY CLASSIFY AT ALL: this is the single fact that decides whether the auth
 * refresh race is real. An 'auth' failure means a request went out with a
 * token that expired mid-flight; a 'network' failure means the device lost
 * connectivity; a 'postgrest' failure means the query itself was rejected.
 * Those are three different bugs and they were previously indistinguishable.
 */
export type LoadFailureKind = 'auth' | 'network' | 'postgrest' | 'unknown'

interface Errorish {
  message?: unknown
  status?: unknown
  code?: unknown
  name?: unknown
}

function textOf(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  const e = err as Errorish | null
  if (e && typeof e.message === 'string') return e.message
  return ''
}

function statusOf(err: unknown): number | null {
  const e = err as Errorish | null
  if (e && typeof e.status === 'number') return e.status
  return null
}

export function classifyLoadError(err: unknown): LoadFailureKind {
  const status = statusOf(err)
  const text = textOf(err).toLowerCase()
  const code = String((err as Errorish | null)?.code ?? '')

  // Auth first: a 401 is unambiguous, and supabase-js also surfaces expiry as
  // a message rather than a status depending on where it failed.
  if (status === 401) return 'auth'
  if (
    text.includes('jwt expired') ||
    text.includes('jwt is expired') ||
    text.includes('invalid jwt') ||
    text.includes('token is expired') ||
    text.includes('refresh_token_not_found') ||
    code === 'PGRST301' // PostgREST: JWT expired
  ) {
    return 'auth'
  }

  // A dropped connection. TypeError('Failed to fetch') is what fetch throws
  // when the request never reached a server at all.
  if (
    text.includes('failed to fetch') ||
    text.includes('networkerror') ||
    text.includes('network request failed') ||
    text.includes('load failed')
  ) {
    return 'network'
  }

  // PostgREST answered and refused: a real query or policy problem.
  if (code.startsWith('PGRST') || code.startsWith('22') || code.startsWith('23')) return 'postgrest'
  if (status !== null && status >= 400) return 'postgrest'

  return 'unknown'
}

/** True for the one failure worth transparently recovering from. */
export function isExpiredSession(err: unknown): boolean {
  return classifyLoadError(err) === 'auth'
}

export interface LoadFailureContext {
  /** The LoadErrorCard label, so a report names the screen a merchant saw. */
  label: string
  /** Extra breadcrumbs the page can add. */
  extra?: Record<string, unknown>
}

/**
 * Report a load failure to Sentry with everything needed to act on it.
 *
 * NEVER THROWS. A reporting failure must not become a second error on a path
 * that is already handling one.
 */
export function reportLoadFailure(err: unknown, ctx: LoadFailureContext): LoadFailureKind {
  const kind = classifyLoadError(err)
  try {
    captureException(err instanceof Error ? err : new Error(textOf(err) || 'load failed'), {
      tags: {
        load_failure: 'true',
        load_kind: kind,
        build_id: BUILD_ID,
        surface: ctx.label,
      },
      extra: {
        build_id: BUILD_ID,
        label: ctx.label,
        http_status: statusOf(err),
        pg_code: (err as Errorish | null)?.code ?? null,
        raw_message: textOf(err),
        ...ctx.extra,
      },
    })
  } catch {
    // Sentry unavailable or disabled. The caller still handles the failure.
  }
  return kind
}

/**
 * Run a page's load, transparently surviving an expired session.
 *
 * THE ONE CASE HANDLED HERE, AND WHY IT IS HERE AND NOT IN THE CARD: if the
 * session expired mid-flight, the fix is not to try the same request again —
 * it is to get a new token first. supabase-js refreshes in the background, so
 * a request can go out with a token that expires before it lands. Refreshing
 * and retrying ONCE turns that into a completely invisible hiccup, and the
 * merchant never sees an error screen at all.
 *
 * EXACTLY ONE EXTRA ATTEMPT. If the refresh fails, or the retry fails too, the
 * error is reported and rethrown so the page shows LoadErrorCard as it always
 * has and the card's own bounded retry takes over. It cannot loop.
 */
export async function loadWithRecovery<T>(
  run: () => Promise<T>,
  ctx: LoadFailureContext,
): Promise<T> {
  try {
    return await run()
  } catch (err) {
    if (!isExpiredSession(err)) {
      reportLoadFailure(err, ctx)
      throw err
    }

    // An expired session. Refresh, then give the load exactly one more go.
    try {
      const { error: refreshError } = await supabase.auth.refreshSession()
      if (refreshError) throw refreshError
    } catch (refreshErr) {
      reportLoadFailure(refreshErr, {
        label: ctx.label,
        extra: { ...ctx.extra, phase: 'session_refresh', original: textOf(err) },
      })
      throw err
    }

    try {
      return await run()
    } catch (retryErr) {
      reportLoadFailure(retryErr, {
        label: ctx.label,
        extra: { ...ctx.extra, phase: 'after_session_refresh' },
      })
      throw retryErr
    }
  }
}
